import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSidebarSettings } from '@/hooks/useSidebarSettings';
import { AdSlot } from './AdSlot';
import { NewsletterCTA } from './NewsletterCTA';

interface ArticleSidebarProps {
  articleId: string;
  categoryId?: string | null;
  tagIds?: string[];
  isMobile?: boolean;
}

function sanitizeSidebarHtml(html: string): string {
  return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
}

function DontMissBlock({
  articleId,
  categoryId,
  tagIds = [],
  source,
  count,
  title,
}: {
  articleId: string;
  categoryId?: string | null;
  tagIds?: string[];
  source: string;
  count: number;
  title: string;
}) {
  const { data: articles = [] } = useQuery({
    queryKey: ['dont-miss', articleId, source, count],
    queryFn: async () => {
      let results: any[] = [];

      if (source === 'related') {
        // Try by tags first
        if (tagIds.length > 0) {
          const { data: tagArticles } = await supabase
            .from('article_tags')
            .select('article_id')
            .in('tag_id', tagIds)
            .neq('article_id', articleId);

          if (tagArticles && tagArticles.length > 0) {
            const ids = [...new Set(tagArticles.map((t: any) => t.article_id))];
            const { data } = await supabase
              .from('articles')
              .select('id, title, slug, featured_image, publish_date')
              .in('id', ids)
              .eq('status', 'published')
              .order('publish_date', { ascending: false })
              .limit(count);
            results = data || [];
          }
        }

        // Fill with category if not enough
        if (results.length < count && categoryId) {
          const existingIds = results.map((r) => r.id);
          const { data } = await supabase
            .from('articles')
            .select('id, title, slug, featured_image, publish_date')
            .eq('status', 'published')
            .eq('category_id', categoryId)
            .neq('id', articleId)
            .not('id', 'in', `(${existingIds.join(',')})`)
            .order('publish_date', { ascending: false })
            .limit(count - results.length);
          if (data) results = [...results, ...data];
        }

        // Fill with latest
        if (results.length < count) {
          const existingIds = results.map((r) => r.id);
          const { data } = await supabase
            .from('articles')
            .select('id, title, slug, featured_image, publish_date')
            .eq('status', 'published')
            .neq('id', articleId)
            .not('id', 'in', `(${[...existingIds].join(',') || 'null'})`)
            .order('publish_date', { ascending: false })
            .limit(count - results.length);
          if (data) results = [...results, ...data];
        }
      } else if (source === 'popular') {
        const { data } = await supabase
          .from('articles')
          .select('id, title, slug, featured_image, publish_date')
          .eq('status', 'published')
          .neq('id', articleId)
          .order('view_count', { ascending: false })
          .limit(count);
        results = data || [];
      } else {
        // latest
        const { data } = await supabase
          .from('articles')
          .select('id, title, slug, featured_image, publish_date')
          .eq('status', 'published')
          .neq('id', articleId)
          .order('publish_date', { ascending: false })
          .limit(count);
        results = data || [];
      }

      return results;
    },
    enabled: !!articleId,
  });

  if (articles.length === 0) return null;

  return (
    <div>
      <h3 className="font-serif text-lg font-bold mb-4">{title}</h3>
      <div className="space-y-4">
        {articles.map((a: any) => (
          <Link
            key={a.id}
            to={`/article/${a.slug}`}
            className="group flex gap-3 items-start"
          >
            {a.featured_image && (
              <img
                src={a.featured_image}
                alt={a.title}
                className="w-20 h-14 object-cover rounded-md flex-shrink-0"
                loading="lazy"
                decoding="async"
                width={80}
                height={56}
              />
            )}
            <p className="text-sm font-medium line-clamp-2 group-hover:text-title-hover transition-colors">
              {a.title}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ArticleSidebar({ articleId, categoryId, tagIds = [], isMobile = false }: ArticleSidebarProps) {
  const { data: settings } = useSidebarSettings();

  if (!settings) return null;

  // On desktop, if sidebar disabled, don't render
  if (!isMobile && !settings.enable_sidebar) return null;

  const showAds = settings.enable_sidebar_ads && settings.sidebar_ad_html?.trim();
  const showDontMiss = settings.enable_dont_miss;

  const adBlock = showAds ? (
    <div
      className="max-w-full overflow-hidden"
      dangerouslySetInnerHTML={{ __html: sanitizeSidebarHtml(settings.sidebar_ad_html) }}
    />
  ) : null;

  const dontMissBlock = showDontMiss ? (
    <DontMissBlock
      articleId={articleId}
      categoryId={categoryId}
      tagIds={tagIds}
      source={settings.dont_miss_source}
      count={settings.dont_miss_count}
      title={settings.dont_miss_title}
    />
  ) : null;

  if (isMobile) {
    // Render as stacked sections
    return (
      <div className="space-y-8 lg:hidden">
        {settings.sidebar_ad_position === 'top' && adBlock}
        {dontMissBlock}
        {settings.sidebar_ad_position === 'bottom' && adBlock}
        {settings.sidebar_ad_position === 'between' && adBlock}
      </div>
    );
  }

  // Desktop sidebar
  return (
    <aside className="hidden lg:block lg:col-span-4">
      <div className="sticky top-24 space-y-6">
        {settings.sidebar_ad_position === 'top' && adBlock}
        <AdSlot slotId="article-sidebar" />
        {settings.sidebar_ad_position === 'between' && adBlock}
        {dontMissBlock}
        <NewsletterCTA />
        {settings.sidebar_ad_position === 'bottom' && adBlock}
      </div>
    </aside>
  );
}
